import { Directive, ElementRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatExpansionPanel } from '@angular/material/expansion';

/**
 * WCAG 2.4.3: when a user expands a `mat-expansion-panel`, move focus into the
 * newly revealed content so keyboard/screen-reader users are made aware that
 * content appeared and land at the start of it.
 *
 * Guard: focus is only moved when the expansion was user-initiated (the panel
 * header currently holds focus). This deliberately skips programmatic/initial
 * auto-expansion (e.g. `[expanded]="isFirst"` on page load), which must not
 * steal focus.
 */
@Directive({
  selector: 'mat-expansion-panel[appFocusContentOnOpen]',
  standalone: true,
})
export class FocusContentOnOpenDirective {
  private readonly host = inject(ElementRef<HTMLElement>);

  private readonly panel = inject(MatExpansionPanel);

  constructor() {
    this.panel.afterExpand
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.focusContent());
  }

  private focusContent(): void {
    const header = this.host.nativeElement.querySelector(
      'mat-expansion-panel-header',
    );

    // Only move focus for user-initiated expansions (header is focused).
    if (!header || document.activeElement !== header) {
      return;
    }

    // Material renders the panel content in a `role="region"` landmark
    // (see MatExpansionPanel's template) — a stable, documented attribute
    // to key off, unlike its internal CSS class names.
    const body = this.host.nativeElement.querySelector('[role="region"]');
    const focusable = body?.querySelector(
      '[tabindex="0"], a[href], button:not([disabled]), input, select, textarea',
    ) as HTMLElement | null;
    focusable?.focus({ preventScroll: true });
  }
}
